import { FormRow } from '@creditornot/cb-form';
import { cbSemanticCssVars, typographyCss } from '@creditornot/cb-ingredients';
import { InputAlert } from '@creditornot/cb-input';
import { SegmentedControl } from '@creditornot/cb-segmented-control';
import { Switch } from '@creditornot/cb-switch';
import React, { useContext, useRef } from 'react';
import { Field } from 'react-final-form';
import { useIntl, FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import * as yup from 'yup';

import { RFFFieldErrorWrapper } from 'common/components';
import InputNumber from 'common/components/InputNumber';
import { useLocaleUtils } from 'common/hooks';
import { MenuFeaturesContext } from 'modules/menu/components/MenuFeaturesContext';

export type FormValues = {
  inventoryMode: 'limited_quantity' | 'inventory' | 'binary';
  inStock: boolean;
  inventoryBalance?: number;
};

type Props = {
  inventoryMode: 'limited_quantity' | 'inventory' | 'binary';
  isWeightBasedItem: boolean;
  disabled?: boolean;
  reservedItems?: number;
  availableItems?: number;
  className?: string;
  sellByWeightInputType: 'grams' | 'number_of_items' | undefined;
};

type ItemQuantityProps = {
  isWeightBasedItem: boolean;
  sellByWeightInputType: 'grams' | 'number_of_items' | undefined;
  quantity?: number;
};

const Root = styled.div``;

const Section = styled.div`
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

const Header = styled.div`
  ${typographyCss.Title1};
  padding: 24px 0;
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

const SectionInfo = styled.div`
  margin-block-start: 8px;
  ${typographyCss.Small};
  font-weight: normal;
  white-space: normal;
`;

const StyledFormRow = styled(FormRow)`
  border-block-start: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

const StyledInput = styled(InputNumber)`
  max-width: 392px;
`;

const InventoryData = styled.div`
  ${typographyCss.Small};
  margin-block-start: 12px;
`;

const InventoryItemQuantity = ({ quantity, sellByWeightInputType, isWeightBasedItem }: ItemQuantityProps) => {
  const { formatWeightQuantity } = useLocaleUtils();

  return (
    <>
      {sellByWeightInputType === 'grams' && quantity !== undefined && isWeightBasedItem
        ? formatWeightQuantity(quantity, {
            unitDisplay: 'narrow',
          })
        : quantity}
    </>
  );
};

export const validationSchema: yup.SchemaOf<FormValues> = yup
  .object({
    inventoryMode: yup.mixed().oneOf(['limited_quantity', 'inventory', 'binary']).required(),
    inStock: yup.boolean().required(),
    inventoryBalance: yup
      .number()
      .integer()
      .min(0)
      .when('inventoryMode', {
        is: (inventoryMode: 'limited_quantity' | 'inventory' | 'binary') => inventoryMode !== 'binary',
        then: schema => schema.required(),
      }),
  })
  .required();

const InventoryMngmtFormSection = ({
  inventoryMode,
  isWeightBasedItem,
  disabled,
  reservedItems,
  availableItems,
  className,
  sellByWeightInputType,
}: Props) => {
  const intl = useIntl();
  const stockRowRef = useRef<HTMLSpanElement>(null);
  const { getLocalizedUnit } = useLocaleUtils();

  const { isRetail } = useContext(MenuFeaturesContext);

  return (
    <Root className={className}>
      {isRetail && (
        <Section>
          <Header>
            <div>
              <FormattedMessage id="menu-item.inventory-management" />
              <SectionInfo>
                <FormattedMessage id="inventory-management.how-to-use" />
              </SectionInfo>
            </div>
          </Header>

          {inventoryMode === 'limited_quantity' && (
            <FormRow label={intl.formatMessage({ id: 'inventory-management.type' })}>
              <InputAlert variant="info">
                <FormattedMessage id="inventory-management.turn-off-info" />
              </InputAlert>
            </FormRow>
          )}
          {inventoryMode !== 'limited_quantity' && (
            <>
              <FormRow label={intl.formatMessage({ id: 'inventory-management.type' })}>
                <Field name="inventoryMode">
                  {field => (
                    <SegmentedControl
                      {...field.input}
                      options={[
                        { value: 'binary', label: intl.formatMessage({ id: 'inventory-management.binary' }) },
                        {
                          value: 'inventory',
                          label: intl.formatMessage({ id: 'inventory-management.track-count' }),
                        },
                      ]}
                    />
                  )}
                </Field>
              </FormRow>
              {inventoryMode === 'inventory' && (
                <span ref={stockRowRef}>
                  <StyledFormRow label={intl.formatMessage({ id: 'inventory.current-stock' })} required>
                    <Field name="inventoryBalance">
                      {field => (
                        <RFFFieldErrorWrapper field={field}>
                          {input => (
                            <StyledInput
                              {...input}
                              disabled={disabled}
                              suffixText={
                                sellByWeightInputType === 'grams' && isWeightBasedItem
                                  ? getLocalizedUnit('gram')
                                  : undefined
                              }
                            />
                          )}
                        </RFFFieldErrorWrapper>
                      )}
                    </Field>
                    {reservedItems !== undefined && (
                      <InventoryData>
                        <FormattedMessage id="inventory.reserved-stock" />:{' '}
                        <InventoryItemQuantity
                          quantity={reservedItems}
                          sellByWeightInputType={sellByWeightInputType}
                          isWeightBasedItem={isWeightBasedItem}
                        />
                      </InventoryData>
                    )}
                    {availableItems !== undefined && (
                      <InventoryData>
                        <FormattedMessage id="inventory.available-stock" />:{' '}
                        <InventoryItemQuantity
                          quantity={availableItems}
                          sellByWeightInputType={sellByWeightInputType}
                          isWeightBasedItem={isWeightBasedItem}
                        />
                      </InventoryData>
                    )}
                  </StyledFormRow>
                </span>
              )}

              {inventoryMode === 'binary' && (
                <span ref={stockRowRef}>
                  <StyledFormRow label={intl.formatMessage({ id: 'inventory-management.in-stock' })}>
                    <Field name="inStock" type="checkbox">
                      {field => <Switch {...field.input} />}
                    </Field>
                  </StyledFormRow>
                </span>
              )}
            </>
          )}
        </Section>
      )}

      <Section>
        <Header>
          <div>
            <FormattedMessage id="menu-item.limited-quantity" />
            <SectionInfo>
              <FormattedMessage id="limited-quantity.how-to-use" />
            </SectionInfo>
          </div>
        </Header>

        <FormRow label={intl.formatMessage({ id: 'limited-quantity.use' })}>
          <Field name="inventoryMode" type="checkbox">
            {field => (
              <Switch
                {...field.input}
                onChange={event => {
                  field.input.onChange(event.currentTarget.checked ? 'limited_quantity' : 'binary');
                  setTimeout(() => stockRowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 0);
                }}
                checked={inventoryMode === 'limited_quantity'}
                disabled={disabled}
              />
            )}
          </Field>
        </FormRow>

        {inventoryMode === 'limited_quantity' && (
          <span ref={stockRowRef}>
            <StyledFormRow label={intl.formatMessage({ id: 'inventory.current' })} required>
              <Field name="inventoryBalance">
                {field => (
                  <RFFFieldErrorWrapper field={field}>
                    {input => (
                      <StyledInput
                        {...input}
                        disabled={disabled}
                        suffixText={
                          sellByWeightInputType === 'grams' && isWeightBasedItem ? getLocalizedUnit('gram') : undefined
                        }
                      />
                    )}
                  </RFFFieldErrorWrapper>
                )}
              </Field>
              {reservedItems !== undefined && (
                <InventoryData>
                  <FormattedMessage id="inventory.reserved" />:{' '}
                  <InventoryItemQuantity
                    quantity={reservedItems}
                    sellByWeightInputType={sellByWeightInputType}
                    isWeightBasedItem={isWeightBasedItem}
                  />
                </InventoryData>
              )}
              {availableItems !== undefined && (
                <InventoryData>
                  <FormattedMessage id="inventory.available" />:{' '}
                  <InventoryItemQuantity
                    quantity={availableItems}
                    sellByWeightInputType={sellByWeightInputType}
                    isWeightBasedItem={isWeightBasedItem}
                  />
                </InventoryData>
              )}
            </StyledFormRow>
          </span>
        )}
      </Section>
    </Root>
  );
};

export default InventoryMngmtFormSection;
